import {
  Badge,
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  Input,
  Label,
  Separator,
  Skeleton,
  cn,
} from '@parcel/ui';
import { type QueryClient, useQueryClient } from '@tanstack/react-query';
import { useRouterState } from '@tanstack/react-router';
import { AtSign, Check, ChevronDown, Copy, Plus, Upload, X } from 'lucide-react';
import * as React from 'react';
import type { ReactNode } from 'react';
import { useAuth } from '../../lib/auth.js';
import { type ThemePreference, useTheme } from '../../lib/theme.js';
import {
  type UserProfile,
  type WorkspaceSummary,
  addWorkspaceDomain,
  getUserProfile,
  getWorkspace,
  getWorkspaceSecurityDomains,
  getWorkspaceSlugFromPath,
  removeWorkspaceDomain,
  updateUserProfile,
  updateWorkspace,
  uploadUserAvatar,
  uploadWorkspaceLogo,
} from '../../lib/workspaces.js';
import { AuthenticatedImage } from '../auth/authenticated-image.js';
import {
  type ProfileDefaults,
  getProfileAvatarDisplay,
  getProfileDefaults,
} from './profile-settings.js';
import {
  formatApprovedDomainCount,
  getSecurityDomainDefaults,
  getSecurityDomainValidation,
} from './security-settings.js';
import { ListSkeleton } from './settings-skeletons.js';
import { SpecPrivacySettings } from './spec-privacy-settings.js';
import {
  getWorkspaceDefaults,
  getWorkspaceDisplayDomain,
  getWorkspaceFiscalYearStart,
  getWorkspaceLogoDisplay,
} from './workspace-settings.js';

export function SettingsShell({
  children,
  wide,
  fill,
}: {
  children: ReactNode;
  wide?: boolean;
  /** On large screens the page holds still and its own panes scroll. */
  fill?: boolean;
}) {
  return (
    // The window scrolls the app; a filled page takes the viewport below the 3.5rem top bar instead.
    <main
      className={cn(
        'min-w-0 flex-1 overflow-auto',
        fill && 'lg:h-[calc(100svh-3.5rem)] lg:flex-none lg:overflow-hidden',
      )}
    >
      {/*
        5xl, not 4xl. At 4xl the column is 896px, so a three-up stat row on
        Usage gives each card ~229px of content -- less than a label and an
        outlined button need side by side, which is what was clipping "Credit
        balance" to "Credit bal...". 5xl gives each card ~272px, which fits.
        Every settings page shares this column, and they all read fine wider.
      */}
      <div
        className={cn(
          'mx-auto w-full px-6 py-8 md:px-10',
          wide || fill ? 'max-w-7xl' : 'max-w-5xl',
          fill && 'lg:flex lg:h-full lg:flex-col lg:pb-0',
        )}
      >
        {children}
      </div>
    </main>
  );
}

export function SettingsPageHeader({
  eyebrow,
  title,
  description,
  action,
}: {
  eyebrow: string;
  title: string;
  description: string;
  /** A link-like control aligned right on the description line. */
  action?: ReactNode;
}) {
  return (
    <header className="mb-8 space-y-2">
      <p className="text-sm font-medium text-muted-foreground">{eyebrow}</p>
      <h2 className="text-3xl font-semibold tracking-tight">{title}</h2>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="max-w-2xl text-sm leading-6 text-muted-foreground">{description}</p>
        {action}
      </div>
    </header>
  );
}

export function SettingsPanel({
  children,
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('overflow-hidden rounded-lg border', className)} {...props}>
      {children}
    </div>
  );
}

function SettingsRow({
  label,
  description,
  htmlFor,
  children,
}: {
  label: string;
  description?: string;
  htmlFor?: string;
  children: ReactNode;
}) {
  return (
    <div className="grid gap-4 p-4 sm:grid-cols-[1fr_16rem] sm:items-center">
      <div>
        {htmlFor ? (
          <Label htmlFor={htmlFor}>{label}</Label>
        ) : (
          <h3 className="text-sm font-medium">{label}</h3>
        )}
        {description ? <p className="mt-1 text-sm text-muted-foreground">{description}</p> : null}
      </div>
      <div className="flex sm:justify-end">{children}</div>
    </div>
  );
}

export function CopyableId({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex w-full items-center gap-2 sm:justify-end">
      <code className="truncate rounded bg-muted px-1.5 py-0.5 font-mono text-sm">{value}</code>
      <Button type="button" variant="ghost" size="icon" aria-label={label} onClick={handleCopy}>
        {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
      </Button>
    </div>
  );
}

function SettingsPanelActions({ children }: { children: ReactNode }) {
  return <div className="flex items-center justify-end gap-3 p-4">{children}</div>;
}

const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function isApiAssetUrl(src: string): boolean {
  return src.startsWith('/api/v1/');
}

function isRevocableLocalUrl(src: string | null): src is string {
  return Boolean(src?.startsWith('blob:'));
}

function SettingsAssetImage({
  src,
  alt,
  className,
}: {
  src: string;
  alt: string;
  className?: string;
}) {
  if (isApiAssetUrl(src)) {
    return <AuthenticatedImage src={src} alt={alt} className={className} />;
  }

  return <img src={src} alt={alt} className={className} />;
}

type ProfileSettingsProfile = Pick<
  ProfileDefaults,
  'firstName' | 'lastName' | 'displayName' | 'title'
>;
type ProfileSettingsApiProfile = Pick<
  UserProfile,
  'firstName' | 'lastName' | 'displayName' | 'title'
>;

function getEmptyProfileSettingsProfile(): ProfileSettingsProfile {
  return {
    firstName: '',
    lastName: '',
    displayName: '',
    title: '',
  };
}

function getPersistedProfileSettingsProfile(
  profile: ProfileSettingsApiProfile,
): ProfileSettingsProfile {
  return {
    firstName: profile.firstName ?? '',
    lastName: profile.lastName ?? '',
    displayName: profile.displayName ?? '',
    title: profile.title ?? '',
  };
}

export function getLoadedProfileSettingsState(
  profile: ProfileSettingsApiProfile,
  defaults: ProfileSettingsProfile,
): {
  formProfile: ProfileSettingsProfile;
  savedProfile: ProfileSettingsProfile;
} {
  const savedProfile = getPersistedProfileSettingsProfile(profile);

  return {
    formProfile: {
      firstName: profile.firstName ?? defaults.firstName,
      lastName: profile.lastName ?? defaults.lastName,
      displayName: savedProfile.displayName,
      title: savedProfile.title,
    },
    savedProfile,
  };
}

export function isProfileSettingsDirty(
  formProfile: ProfileSettingsProfile,
  savedProfile: ProfileSettingsProfile,
): boolean {
  return (
    formProfile.firstName !== savedProfile.firstName ||
    formProfile.lastName !== savedProfile.lastName ||
    formProfile.displayName !== savedProfile.displayName ||
    formProfile.title !== savedProfile.title
  );
}

export function setCachedUserProfile(queryClient: QueryClient, profile: UserProfile): void {
  queryClient.setQueryData<UserProfile>(['me'], profile);
}

export function ProfileSettingsPage() {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const metadata = session?.user.user_metadata;
  const metadataAvatarUrl =
    typeof metadata?.avatar_url === 'string'
      ? metadata.avatar_url
      : typeof metadata?.avatarUrl === 'string'
        ? metadata.avatarUrl
        : null;
  const metadataDisplayName =
    typeof metadata?.displayName === 'string'
      ? metadata.displayName
      : typeof metadata?.display_name === 'string'
        ? metadata.display_name
        : typeof metadata?.nickname === 'string'
          ? metadata.nickname
          : undefined;
  const profileDefaults = getProfileDefaults({
    email: session?.user.email,
    metadataTitle: typeof metadata?.title === 'string' ? metadata.title : undefined,
  });
  const defaults = {
    ...profileDefaults,
    displayName: metadataDisplayName?.trim() || profileDefaults.displayName,
  };
  const [firstName, setFirstName] = React.useState(defaults.firstName);
  const [lastName, setLastName] = React.useState(defaults.lastName);
  const [displayName, setDisplayName] = React.useState(defaults.displayName);
  const [title, setTitle] = React.useState(defaults.title);
  const [avatarUrl, setAvatarUrl] = React.useState<string | null>(metadataAvatarUrl);
  const [savedProfile, setSavedProfile] = React.useState<ProfileSettingsProfile>(() =>
    getEmptyProfileSettingsProfile(),
  );
  const [savingProfile, setSavingProfile] = React.useState(false);
  // The fields hold skeletons until the stored profile lands, so blank names never flash.
  const [profileLoaded, setProfileLoaded] = React.useState(false);
  const [profileError, setProfileError] = React.useState<string | null>(null);
  const [saved, setSaved] = React.useState(false);
  const [leaveWorkspaceOpen, setLeaveWorkspaceOpen] = React.useState(false);
  const avatarDisplay = getProfileAvatarDisplay(avatarUrl, {
    displayName,
    firstName,
    lastName,
    email: defaults.email,
  });
  const formProfile = { firstName, lastName, displayName, title };
  const isDirty = isProfileSettingsDirty(formProfile, savedProfile);
  const canSaveProfile =
    isDirty && firstName.trim().length > 0 && lastName.trim().length > 0 && !savingProfile;

  React.useEffect(() => {
    let active = true;

    async function loadProfile() {
      try {
        const profile = await getUserProfile();
        if (!active) return;

        const nextProfile = getLoadedProfileSettingsState(profile, {
          firstName: defaults.firstName,
          lastName: defaults.lastName,
          displayName: '',
          title: '',
        });
        setFirstName(nextProfile.formProfile.firstName);
        setLastName(nextProfile.formProfile.lastName);
        setDisplayName(nextProfile.formProfile.displayName);
        setTitle(nextProfile.formProfile.title);
        setAvatarUrl(profile.avatarUrl ?? metadataAvatarUrl);
        setSavedProfile(nextProfile.savedProfile);
        setProfileError(null);
        setSaved(false);
        setProfileLoaded(true);
      } catch (err) {
        if (active) {
          setProfileError(err instanceof Error ? err.message : 'Unable to load profile.');
          setProfileLoaded(true);
        }
      }
    }

    void loadProfile();

    return () => {
      active = false;
    };
  }, [defaults.firstName, defaults.lastName, metadataAvatarUrl]);

  React.useEffect(() => {
    return () => {
      if (isRevocableLocalUrl(avatarUrl)) URL.revokeObjectURL(avatarUrl);
    };
  }, [avatarUrl]);

  const onAvatarChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const file = event.target.files?.[0];
    if (!file) return;

    setSavingProfile(true);
    setProfileError(null);
    setSaved(false);
    try {
      const response = await uploadUserAvatar(file);
      setAvatarUrl(response.avatarUrl);
      void queryClient.invalidateQueries({ queryKey: ['me'] });
    } catch (err) {
      setProfileError(err instanceof Error ? err.message : 'Unable to upload profile picture.');
    } finally {
      setSavingProfile(false);
      input.value = '';
    }
  };

  const saveProfile = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSavingProfile(true);
    setProfileError(null);

    try {
      const profile = await updateUserProfile({ firstName, lastName, displayName, title });
      const nextProfile = getPersistedProfileSettingsProfile(profile);
      setCachedUserProfile(queryClient, profile);
      setFirstName(nextProfile.firstName);
      setLastName(nextProfile.lastName);
      setDisplayName(nextProfile.displayName);
      setTitle(nextProfile.title);
      setAvatarUrl(profile.avatarUrl ?? avatarUrl);
      setSavedProfile(nextProfile);
      setSaved(true);
    } catch (err) {
      setProfileError(err instanceof Error ? err.message : 'Unable to save profile.');
    } finally {
      setSavingProfile(false);
    }
  };

  return (
    <SettingsShell>
      <SettingsPageHeader
        eyebrow="Personal"
        title="Profile"
        description="Manage the identity other members see across your workspace."
      />
      <section className="space-y-10">
        <form onSubmit={(event) => void saveProfile(event)}>
          <SettingsPanel>
            <SettingsRow
              label="Profile picture"
              description="Click the avatar to upload."
              htmlFor="profile-picture"
            >
              <div className="flex flex-col items-end gap-2">
                <Label
                  htmlFor="profile-picture"
                  className="group relative flex size-14 cursor-pointer items-center justify-center overflow-hidden rounded-full border bg-muted text-sm font-semibold text-foreground"
                >
                  {avatarDisplay.kind === 'image' ? (
                    <SettingsAssetImage
                      src={avatarDisplay.src}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    avatarDisplay.initials
                  )}
                  <span className="absolute inset-0 hidden items-center justify-center bg-background/80 group-hover:flex">
                    <Upload className="h-4 w-4" />
                  </span>
                </Label>
                <Input
                  id="profile-picture"
                  type="file"
                  accept="image/*"
                  className="sr-only"
                  onChange={(event) => void onAvatarChange(event)}
                  disabled={savingProfile}
                />
                {savingProfile ? (
                  <p className="text-sm text-muted-foreground">Uploading...</p>
                ) : null}
                {profileError ? <p className="text-sm text-destructive">{profileError}</p> : null}
              </div>
            </SettingsRow>
            <Separator />
            <SettingsRow label="Email">
              <p className="w-full text-sm text-muted-foreground sm:text-right">{defaults.email}</p>
            </SettingsRow>
            <Separator />
            <SettingsRow label="First name" htmlFor="profile-first-name">
              {profileLoaded ? (
                <Input
                  id="profile-first-name"
                  value={firstName}
                  autoComplete="given-name"
                  required
                  onChange={(event) => {
                    setFirstName(event.target.value);
                    setSaved(false);
                  }}
                />
              ) : (
                <Skeleton className="h-9 w-full" data-testid="settings-field-skeleton" />
              )}
            </SettingsRow>
            <Separator />
            <SettingsRow label="Last name" htmlFor="profile-last-name">
              {profileLoaded ? (
                <Input
                  id="profile-last-name"
                  value={lastName}
                  autoComplete="family-name"
                  required
                  onChange={(event) => {
                    setLastName(event.target.value);
                    setSaved(false);
                  }}
                />
              ) : (
                <Skeleton className="h-9 w-full" data-testid="settings-field-skeleton" />
              )}
            </SettingsRow>
            <Separator />
            <SettingsRow
              label="Display name"
              description="Optional name to show in Parcel."
              htmlFor="profile-display-name"
            >
              {profileLoaded ? (
                <Input
                  id="profile-display-name"
                  value={displayName}
                  placeholder={firstName || 'Nick'}
                  autoComplete="nickname"
                  onChange={(event) => {
                    setDisplayName(event.target.value);
                    setSaved(false);
                  }}
                />
              ) : (
                <Skeleton className="h-9 w-full" data-testid="settings-field-skeleton" />
              )}
            </SettingsRow>
            <Separator />
            <SettingsRow label="Title" description="Your job title or role" htmlFor="profile-title">
              {profileLoaded ? (
                <Input
                  id="profile-title"
                  value={title}
                  placeholder="Software engineer"
                  autoComplete="organization-title"
                  onChange={(event) => {
                    setTitle(event.target.value);
                    setSaved(false);
                  }}
                />
              ) : (
                <Skeleton className="h-9 w-full" data-testid="settings-field-skeleton" />
              )}
            </SettingsRow>
            <Separator />
            <SettingsPanelActions>
              {saved ? (
                <p className="text-sm text-muted-foreground">Changes saved locally.</p>
              ) : null}
              <Button type="submit" disabled={!canSaveProfile}>
                Save changes
              </Button>
            </SettingsPanelActions>
          </SettingsPanel>
        </form>

        <section className="space-y-4">
          <h3 className="text-base font-medium">Workspace access</h3>
          <SettingsPanel className="flex items-center justify-between gap-4 p-4">
            <div>
              <h4 className="text-sm font-medium">Remove yourself from workspace</h4>
              <p className="mt-1 text-sm text-muted-foreground">
                Leave this workspace and lose access to its projects and settings.
              </p>
            </div>
            <Dialog open={leaveWorkspaceOpen} onOpenChange={setLeaveWorkspaceOpen}>
              <Button
                type="button"
                variant="outline"
                className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                onClick={() => setLeaveWorkspaceOpen(true)}
              >
                Leave workspace
              </Button>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Leave workspace?</DialogTitle>
                  <DialogDescription>
                    You will lose access to this workspace, including its projects, members, and
                    settings. This action is not connected to backend workspace membership yet.
                  </DialogDescription>
                </DialogHeader>
                <div className="flex justify-end gap-2">
                  <DialogClose asChild>
                    <Button type="button" variant="outline">
                      Cancel
                    </Button>
                  </DialogClose>
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => setLeaveWorkspaceOpen(false)}
                  >
                    Confirm leave
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </SettingsPanel>
        </section>
      </section>
    </SettingsShell>
  );
}

export function WorkspaceGeneralSettingsPage() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const workspaceSlug = getWorkspaceSlugFromPath(pathname);
  const queryClient = useQueryClient();
  const workspaceSlugRef = React.useRef<string | null>(workspaceSlug);
  const loadWorkspaceRequestRef = React.useRef(0);
  const saveWorkspaceRequestRef = React.useRef(0);
  const uploadLogoRequestRef = React.useRef(0);
  const defaults = getWorkspaceDefaults();
  const [name, setName] = React.useState(defaults.name);
  const [domain, setDomain] = React.useState(defaults.domain);
  const [fiscalYearStart, setFiscalYearStart] = React.useState(defaults.fiscalYearStart);
  const [logoUrl, setLogoUrl] = React.useState<string | null>(null);
  const [workspaceId, setWorkspaceId] = React.useState<string | null>(null);
  const [savedWorkspace, setSavedWorkspace] = React.useState({
    name: defaults.name,
    fiscalYearStart: defaults.fiscalYearStart,
  });
  const [savingWorkspaceSettings, setSavingWorkspaceSettings] = React.useState(false);
  const [savingWorkspaceLogo, setSavingWorkspaceLogo] = React.useState(false);
  const [canUpdateWorkspace, setCanUpdateWorkspace] = React.useState(false);
  const [canUploadWorkspaceAssets, setCanUploadWorkspaceAssets] = React.useState(false);
  const [workspacePermissionsLoaded, setWorkspacePermissionsLoaded] = React.useState(false);
  const [workspaceError, setWorkspaceError] = React.useState<string | null>(null);
  const [saved, setSaved] = React.useState(false);
  const logoDisplay = getWorkspaceLogoDisplay(logoUrl, name);
  const isDirty =
    name !== savedWorkspace.name || fiscalYearStart !== savedWorkspace.fiscalYearStart;

  workspaceSlugRef.current = workspaceSlug;

  React.useEffect(() => {
    let active = true;
    const expectedWorkspaceSlug = workspaceSlug;
    const requestId = ++loadWorkspaceRequestRef.current;
    saveWorkspaceRequestRef.current += 1;
    uploadLogoRequestRef.current += 1;
    setWorkspaceId(null);
    setCanUpdateWorkspace(false);
    setCanUploadWorkspaceAssets(false);
    setWorkspacePermissionsLoaded(false);
    setSavingWorkspaceSettings(false);
    setSavingWorkspaceLogo(false);
    setSaved(false);

    async function loadWorkspace() {
      if (!workspaceSlug) {
        setWorkspaceError('Select a workspace before updating workspace settings.');
        return;
      }

      try {
        const workspace = await getWorkspace(workspaceSlug);
        if (
          !active ||
          workspaceSlugRef.current !== expectedWorkspaceSlug ||
          loadWorkspaceRequestRef.current !== requestId
        ) {
          return;
        }

        const nextFiscalYearStart = getWorkspaceFiscalYearStart(workspace.settings);
        setName(workspace.name);
        setWorkspaceId(workspace.id);
        setDomain(getWorkspaceDisplayDomain(workspace.companyDomain, workspace.companyDomains));
        setLogoUrl(workspace.logoUrl);
        setFiscalYearStart(nextFiscalYearStart);
        setCanUpdateWorkspace(workspace.capabilities.canUpdateSettings);
        setCanUploadWorkspaceAssets(workspace.capabilities.canUploadAssets);
        setWorkspacePermissionsLoaded(true);
        setSavedWorkspace({
          name: workspace.name,
          fiscalYearStart: nextFiscalYearStart,
        });
        setWorkspaceError(null);
        setSaved(false);
      } catch (err) {
        if (
          active &&
          workspaceSlugRef.current === expectedWorkspaceSlug &&
          loadWorkspaceRequestRef.current === requestId
        ) {
          setCanUpdateWorkspace(false);
          setCanUploadWorkspaceAssets(false);
          setWorkspacePermissionsLoaded(false);
          setWorkspaceError(err instanceof Error ? err.message : 'Unable to load workspace.');
        }
      }
    }

    void loadWorkspace();

    return () => {
      active = false;
    };
  }, [workspaceSlug]);

  React.useEffect(() => {
    return () => {
      if (isRevocableLocalUrl(logoUrl)) URL.revokeObjectURL(logoUrl);
    };
  }, [logoUrl]);

  const onLogoChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const file = event.target.files?.[0];
    if (!file) return;

    if (!workspaceSlug) {
      setWorkspaceError('Select a workspace before uploading a logo.');
      input.value = '';
      return;
    }

    if (!canUploadWorkspaceAssets) {
      input.value = '';
      return;
    }

    setSavingWorkspaceLogo(true);
    setWorkspaceError(null);
    setSaved(false);
    const expectedWorkspaceSlug = workspaceSlug;
    const requestId = ++uploadLogoRequestRef.current;
    try {
      const response = await uploadWorkspaceLogo(workspaceSlug, file);
      if (
        workspaceSlugRef.current !== expectedWorkspaceSlug ||
        uploadLogoRequestRef.current !== requestId
      ) {
        return;
      }

      setLogoUrl(response.logoUrl);
      queryClient.setQueryData<WorkspaceSummary>(
        ['workspace', expectedWorkspaceSlug],
        (currentWorkspace) =>
          currentWorkspace ? { ...currentWorkspace, logoUrl: response.logoUrl } : currentWorkspace,
      );
    } catch (err) {
      if (
        workspaceSlugRef.current === expectedWorkspaceSlug &&
        uploadLogoRequestRef.current === requestId
      ) {
        setWorkspaceError(err instanceof Error ? err.message : 'Unable to upload workspace logo.');
      }
    } finally {
      if (
        workspaceSlugRef.current === expectedWorkspaceSlug &&
        uploadLogoRequestRef.current === requestId
      ) {
        setSavingWorkspaceLogo(false);
      }
      input.value = '';
    }
  };

  const saveWorkspace = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!workspaceSlug || !canUpdateWorkspace) return;

    setSavingWorkspaceSettings(true);
    setWorkspaceError(null);
    setSaved(false);
    const expectedWorkspaceSlug = workspaceSlug;
    const requestId = ++saveWorkspaceRequestRef.current;
    try {
      const workspace = await updateWorkspace(workspaceSlug, {
        name,
        settings: { fiscalYearStart },
      });
      if (
        workspaceSlugRef.current !== expectedWorkspaceSlug ||
        saveWorkspaceRequestRef.current !== requestId
      ) {
        return;
      }

      const nextFiscalYearStart = getWorkspaceFiscalYearStart(workspace.settings);

      setName(workspace.name);
      setWorkspaceId(workspace.id);
      setDomain(getWorkspaceDisplayDomain(workspace.companyDomain, workspace.companyDomains));
      setFiscalYearStart(nextFiscalYearStart);
      setCanUpdateWorkspace(workspace.capabilities.canUpdateSettings);
      setCanUploadWorkspaceAssets(workspace.capabilities.canUploadAssets);
      setWorkspacePermissionsLoaded(true);
      setSavedWorkspace({ name: workspace.name, fiscalYearStart: nextFiscalYearStart });
      queryClient.setQueryData<WorkspaceSummary>(['workspace', expectedWorkspaceSlug], workspace);
      setSaved(true);
    } catch (err) {
      if (
        workspaceSlugRef.current === expectedWorkspaceSlug &&
        saveWorkspaceRequestRef.current === requestId
      ) {
        setWorkspaceError(
          err instanceof Error ? err.message : 'Unable to save workspace settings.',
        );
      }
    } finally {
      if (
        workspaceSlugRef.current === expectedWorkspaceSlug &&
        saveWorkspaceRequestRef.current === requestId
      ) {
        setSavingWorkspaceSettings(false);
      }
    }
  };

  // Placeholder values ("parcel") must never show as the workspace's own; skeletons stand in.
  const loadingWorkspace = !workspacePermissionsLoaded && !workspaceError;

  return (
    <SettingsShell>
      <SettingsPageHeader
        eyebrow="Workspace"
        title="General"
        description="Update workspace identity and defaults for the parcel team."
      />
      <form onSubmit={saveWorkspace} className="space-y-10" aria-busy={loadingWorkspace}>
        <SettingsPanel>
          <SettingsRow
            label="Logo"
            description="Recommended size is 256x256px"
            htmlFor="workspace-logo"
          >
            <div className="flex flex-col items-end gap-2">
              {loadingWorkspace ? (
                <Skeleton className="size-12 rounded-lg" />
              ) : (
                <Label
                  htmlFor="workspace-logo"
                  className="group relative flex size-12 cursor-pointer items-center justify-center overflow-hidden rounded-lg border bg-brand text-lg font-semibold text-brand-foreground"
                >
                  {logoDisplay.kind === 'image' ? (
                    <SettingsAssetImage
                      src={logoDisplay.src}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    logoDisplay.initial
                  )}
                  <span className="absolute inset-0 hidden items-center justify-center bg-background/80 text-foreground group-hover:flex">
                    <Upload className="h-4 w-4" />
                  </span>
                </Label>
              )}
              <Input
                id="workspace-logo"
                type="file"
                accept="image/*"
                className="sr-only"
                onChange={(event) => void onLogoChange(event)}
                disabled={
                  savingWorkspaceLogo || savingWorkspaceSettings || !canUploadWorkspaceAssets
                }
              />
              {savingWorkspaceLogo ? (
                <p className="text-sm text-muted-foreground">Uploading...</p>
              ) : null}
              {workspaceError ? <p className="text-sm text-destructive">{workspaceError}</p> : null}
            </div>
          </SettingsRow>
          <Separator />
          <SettingsRow label="Name" htmlFor="workspace-name">
            {loadingWorkspace ? (
              <Skeleton className="h-9 w-full" data-testid="settings-field-skeleton" />
            ) : (
              <Input
                id="workspace-name"
                value={name}
                onChange={(event) => {
                  setName(event.target.value);
                  setSaved(false);
                }}
                disabled={savingWorkspaceSettings || !canUpdateWorkspace}
              />
            )}
          </SettingsRow>
          <Separator />
          <SettingsRow label="Company domain">
            {loadingWorkspace ? (
              <Skeleton className="h-4 w-40 sm:ml-auto" />
            ) : (
              <p className="w-full text-sm text-muted-foreground sm:text-right">{domain}</p>
            )}
          </SettingsRow>
          <Separator />
          <SettingsRow label="Workspace ID">
            {workspaceId ? (
              <CopyableId value={workspaceId} label="Copy workspace ID" />
            ) : loadingWorkspace ? (
              <Skeleton
                className="h-8 w-72 max-w-full sm:ml-auto"
                data-testid="workspace-id-skeleton"
              />
            ) : (
              <p className="w-full text-sm text-muted-foreground sm:text-right">—</p>
            )}
          </SettingsRow>
          <Separator />
          {workspacePermissionsLoaded && !canUpdateWorkspace ? (
            <p className="px-4 pt-4 text-sm text-muted-foreground">
              You do not have permission to update workspace settings.
            </p>
          ) : null}
          <SettingsPanelActions>
            {saved ? <p className="text-sm text-muted-foreground">Changes saved.</p> : null}
            <Button
              type="submit"
              disabled={
                !isDirty || savingWorkspaceLogo || savingWorkspaceSettings || !canUpdateWorkspace
              }
            >
              Save changes
            </Button>
          </SettingsPanelActions>
        </SettingsPanel>

        <section className="space-y-4">
          <h3 className="text-base font-medium">Time & region</h3>
          <SettingsPanel>
            <SettingsRow
              label="First month of the fiscal year"
              description="Used when grouping projects and issues quarterly, half-yearly, and yearly"
            >
              {loadingWorkspace ? (
                <Skeleton className="h-10 w-40" />
              ) : (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-40 justify-between"
                      disabled={savingWorkspaceSettings || !canUpdateWorkspace}
                    >
                      {fiscalYearStart}
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-40">
                    <DropdownMenuRadioGroup
                      value={fiscalYearStart}
                      onValueChange={(value) => {
                        setFiscalYearStart(value);
                        setSaved(false);
                      }}
                    >
                      {months.map((month) => (
                        <DropdownMenuRadioItem key={month} value={month}>
                          {month}
                        </DropdownMenuRadioItem>
                      ))}
                    </DropdownMenuRadioGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </SettingsRow>
          </SettingsPanel>
        </section>
      </form>
    </SettingsShell>
  );
}

export function SecuritySettingsPage() {
  const { session } = useAuth();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const workspaceSlug = getWorkspaceSlugFromPath(pathname);
  const userEmail = session?.user.email?.toLowerCase() ?? '';
  const [approvedDomains, setApprovedDomains] = React.useState<string[]>([]);
  const [loadingDomains, setLoadingDomains] = React.useState(true);
  const [domainError, setDomainError] = React.useState<string | null>(null);
  const [savingDomain, setSavingDomain] = React.useState(false);
  const [removingDomain, setRemovingDomain] = React.useState<string | null>(null);
  const [addDomainOpen, setAddDomainOpen] = React.useState(false);
  const [domainInput, setDomainInput] = React.useState('');
  const [verificationEmail, setVerificationEmail] = React.useState('');
  const domainValidation = getSecurityDomainValidation(domainInput, approvedDomains);
  const showDomainMessage = domainInput.trim().length > 0 && domainValidation.message;
  const normalizedVerificationEmail = verificationEmail.trim().toLowerCase();
  const verificationEmailMessage =
    verificationEmail.trim().length > 0 && normalizedVerificationEmail !== userEmail
      ? 'Use the email address you signed in with.'
      : null;
  const verificationEmailValid =
    domainValidation.valid &&
    normalizedVerificationEmail === userEmail &&
    userEmail.endsWith(`@${domainValidation.domain}`);

  React.useEffect(() => {
    let active = true;

    async function loadDomains() {
      setLoadingDomains(true);
      setDomainError(null);

      try {
        const domains = workspaceSlug
          ? await getWorkspaceSecurityDomains(workspaceSlug)
          : getSecurityDomainDefaults();
        if (active) setApprovedDomains(domains);
      } catch (err) {
        if (active) {
          setDomainError(err instanceof Error ? err.message : 'Unable to load approved domains.');
        }
      } finally {
        if (active) setLoadingDomains(false);
      }
    }

    void loadDomains();

    return () => {
      active = false;
    };
  }, [workspaceSlug]);

  React.useEffect(() => {
    if (addDomainOpen && !verificationEmail && userEmail) {
      setVerificationEmail(userEmail);
    }
  }, [addDomainOpen, userEmail, verificationEmail]);

  const addDomain = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!domainValidation.valid || !verificationEmailValid || !workspaceSlug) return;

    setSavingDomain(true);
    setDomainError(null);

    try {
      setApprovedDomains(
        await addWorkspaceDomain(workspaceSlug, {
          domain: domainValidation.domain,
          verificationEmail,
        }),
      );
      setDomainInput('');
      setVerificationEmail('');
      setAddDomainOpen(false);
    } catch (err) {
      setDomainError(err instanceof Error ? err.message : 'Unable to add domain.');
    } finally {
      setSavingDomain(false);
    }
  };

  const removeDomain = async (domain: string) => {
    if (!workspaceSlug) return;

    setRemovingDomain(domain);
    setDomainError(null);

    try {
      setApprovedDomains(await removeWorkspaceDomain(workspaceSlug, domain));
    } catch (err) {
      setDomainError(err instanceof Error ? err.message : 'Unable to remove domain.');
    } finally {
      setRemovingDomain(null);
    }
  };

  return (
    <SettingsShell>
      <SettingsPageHeader
        eyebrow="Workspace"
        title="Security"
        description="Manage the approved email domains that can request access to this workspace."
      />

      <section className="space-y-10">
        <section className="space-y-4">
          <div className="space-y-1">
            <h3 className="text-lg font-medium">Workspace access</h3>
            <p className="text-sm text-muted-foreground">
              Choose which company email domains are allowed to request workspace access.
            </p>
            {domainError ? <p className="text-sm text-destructive">{domainError}</p> : null}
          </div>

          <div className="space-y-3">
            <div>
              <h4 className="text-sm font-medium">Approved email domains</h4>
              <p className="mt-1 text-sm text-muted-foreground">
                People with an email address at these domains can request to join this workspace.
              </p>
            </div>
            <SettingsPanel>
              <div className="flex items-center justify-between gap-4 border-b p-4">
                <h5 className="font-medium">{formatApprovedDomainCount(approvedDomains.length)}</h5>
                <Dialog open={addDomainOpen} onOpenChange={setAddDomainOpen}>
                  <DialogTriggerButton onClick={() => setAddDomainOpen(true)} />
                  <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                      <DialogTitle>Add domain</DialogTitle>
                      <DialogDescription>
                        Add a company email domain that can request access to this workspace.
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={addDomain} className="space-y-5">
                      <div className="space-y-2">
                        <Label htmlFor="security-domain">Domain</Label>
                        <Input
                          id="security-domain"
                          value={domainInput}
                          onChange={(event) => setDomainInput(event.target.value)}
                          placeholder="acme.com"
                          autoComplete="off"
                          autoFocus
                        />
                        {showDomainMessage ? (
                          <p className="text-sm text-destructive">{domainValidation.message}</p>
                        ) : null}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="security-verification-email">Email for verification</Label>
                        <Input
                          id="security-verification-email"
                          type="email"
                          value={verificationEmail}
                          onChange={(event) => setVerificationEmail(event.target.value)}
                          placeholder="Email address for that domain"
                        />
                        {verificationEmailMessage ? (
                          <p className="text-sm text-destructive">{verificationEmailMessage}</p>
                        ) : null}
                      </div>
                      <div className="flex justify-end gap-3">
                        <DialogClose asChild>
                          <Button type="button" variant="outline">
                            Cancel
                          </Button>
                        </DialogClose>
                        <Button
                          type="submit"
                          disabled={
                            !domainValidation.valid || !verificationEmailValid || savingDomain
                          }
                        >
                          {savingDomain ? 'Adding…' : 'Add domain'}
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              {loadingDomains ? (
                <ListSkeleton rows={2} />
              ) : approvedDomains.length === 0 ? (
                <div className="p-4 text-sm text-muted-foreground">No domains approved yet.</div>
              ) : (
                <div className="divide-y">
                  {approvedDomains.map((domain) => (
                    <div key={domain} className="flex items-center gap-4 p-4">
                      <div className="flex size-11 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                        <AtSign className="size-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium">{domain}</p>
                        <p className="text-sm text-muted-foreground">
                          Approved for workspace access
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={`Remove ${domain}`}
                        disabled={removingDomain === domain}
                        onClick={() => void removeDomain(domain)}
                      >
                        <X className="size-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </SettingsPanel>
          </div>
        </section>

        <section className="space-y-3">
          <div>
            <h3 className="text-lg font-medium">Authentication methods</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Admins and guests can always authenticate via email.
            </p>
          </div>
          <SettingsPanel>
            <SettingsRow
              label="Email authentication"
              description="Members can sign in with a magic link sent to their email."
            >
              <Badge variant="secondary">Enabled</Badge>
            </SettingsRow>
            <Separator />
            <SettingsRow label="Single sign-on" description="SAML and SCIM setup will appear here.">
              <Badge variant="outline">Coming soon</Badge>
            </SettingsRow>
          </SettingsPanel>
        </section>

        <SpecPrivacySettings pathname={pathname} />
      </section>
    </SettingsShell>
  );
}

function DialogTriggerButton({ onClick }: { onClick: () => void }) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      aria-label="Add approved domain"
      onClick={onClick}
    >
      <Plus className="size-4" />
    </Button>
  );
}

const themeOptions: Array<{ value: ThemePreference; label: string; description: string }> = [
  { value: 'light', label: 'Light', description: 'Use the light interface.' },
  { value: 'dark', label: 'Dark', description: 'Use the dark interface.' },
  { value: 'system', label: 'System', description: 'Follow your device setting.' },
];

export function AppearanceSettingsPage() {
  const { theme, setTheme } = useTheme();

  return (
    <SettingsShell>
      <SettingsPageHeader
        eyebrow="Workspace"
        title="Appearance"
        description="Choose how parcel looks in this browser."
      />
      <section className="space-y-3">
        {themeOptions.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => setTheme(option.value)}
            className={cn(
              'flex w-full max-w-xl items-center justify-between gap-4 rounded-lg border p-4 text-left transition-colors hover:bg-accent',
              theme === option.value && 'border-foreground',
            )}
          >
            <span>
              <span className="block font-medium">{option.label}</span>
              <span className="block text-sm text-muted-foreground">{option.description}</span>
            </span>
            {theme === option.value ? <Badge>Selected</Badge> : null}
          </button>
        ))}
      </section>
    </SettingsShell>
  );
}
